import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { phoneTelHref, emailMailtoHref } from '../utils/contactLinks';
import { useAuth } from '../context/AuthContext';
import { bedService, floorService, hostelService, roomService } from '../services/api';
import { Hostel } from '../types';
import './HostelProfile.css';

type OverviewStats = {
  floors: number;
  rooms: number;
  totalBeds: number;
  occupiedBeds: number;
  availableBeds: number;
};

function DetailRow({
  label,
  value,
  full,
  href,
}: {
  label: string;
  value?: string | number | null;
  full?: boolean;
  href?: string;
}) {
  const text = value === undefined || value === null || String(value).trim() === '' ? '—' : String(value);
  return (
    <div className={`hostel-profile-detail${full ? ' hostel-profile-detail--full' : ''}`}>
      <span className="hostel-profile-detail-label">{label}</span>
      {href && text !== '—' ? (
        <a className="hostel-profile-detail-value" href={href}>
          {text}
        </a>
      ) : (
        <span className="hostel-profile-detail-value">{text}</span>
      )}
    </div>
  );
}

const Profile: React.FC = () => {
  const { hostel: ctxHostel, isHostelAdmin } = useAuth();
  const navigate = useNavigate();
  const [hostel, setHostel] = useState<Hostel | null>(null);
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadHostel = useCallback(async () => {
    if (!ctxHostel?.id) {
      setHostel(null);
      setOverview(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const hid = ctxHostel.id;
      const [data, floorsRes, roomsRes, bedsRes] = await Promise.all([
        hostelService.getHostelById(hid),
        floorService.getFloorsByHostel(hid),
        roomService.getRoomsByHostel(hid),
        bedService.getBedsByHostel(hid),
      ]);
      setHostel(data);

      const floors = floorsRes.filter((f) => f.isActive !== false);
      const rooms = roomsRes.filter((r) => r.isActive !== false);
      const beds = bedsRes.filter((b) => b.isActive !== false);
      const occupiedBeds = beds.filter((b) => String(b.bedStatus).toUpperCase() === 'OCCUPIED').length;
      const totalBeds = beds.length;

      setOverview({
        floors: floors.length,
        rooms: rooms.length,
        totalBeds,
        occupiedBeds,
        availableBeds: Math.max(0, totalBeds - occupiedBeds),
      });
    } catch {
      toast.error('Failed to load hostel details');
      setHostel(ctxHostel);
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [ctxHostel]);

  useEffect(() => {
    void loadHostel();
  }, [loadHostel]);

  if (!ctxHostel) {
    return (
      <div className="hostel-profile-page">
        <p>No hostel selected. Switch or choose a hostel from the menu.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading hostel profile…</div>;
  }

  if (!hostel) {
    return <div className="no-data">Hostel not found</div>;
  }

  const statusClass =
    hostel.status === 'ACTIVE'
      ? 'hostel-profile-status--active'
      : hostel.status === 'SUSPENDED'
        ? 'hostel-profile-status--suspended'
        : 'hostel-profile-status--inactive';

  const addressLine = [hostel.address, hostel.city, hostel.state, hostel.pincode, hostel.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="hostel-profile-page">
      <header className="hostel-profile-header">
        <div className="hostel-profile-hero">
          <div className="hostel-profile-logo-wrap">
            {hostel.logo ? (
              <img src={hostel.logo} alt="" className="hostel-profile-logo" />
            ) : (
              <span className="hostel-profile-logo--placeholder" aria-hidden>
                🏢
              </span>
            )}
          </div>
          <div className="hostel-profile-title-block">
            <h1>{hostel.name}</h1>
            {hostel.hostelCode ? <p className="hostel-profile-subtitle">Code: {hostel.hostelCode}</p> : null}
            {hostel.description ? <p className="hostel-profile-subtitle">{hostel.description}</p> : null}
            <span className={`hostel-profile-status ${statusClass}`}>{hostel.status || 'ACTIVE'}</span>
          </div>
        </div>
        {isHostelAdmin() ? (
          <button type="button" className="hostel-profile-edit-btn" onClick={() => navigate('/profile/edit')}>
            Edit details
          </button>
        ) : null}
      </header>

      <section className="hostel-profile-card">
        <h2>Contact</h2>
        <div className="hostel-profile-grid">
          <DetailRow label="Phone" value={hostel.phone} href={phoneTelHref(hostel.phone) ?? undefined} />
          <DetailRow label="Email" value={hostel.email} href={emailMailtoHref(hostel.email) ?? undefined} />
          <DetailRow label="Website" value={hostel.websiteUrl} href={hostel.websiteUrl || undefined} />
          <DetailRow label="Address" value={addressLine || hostel.address} full />
        </div>
      </section>

      <section className="hostel-profile-card">
        <h2>Business</h2>
        <div className="hostel-profile-grid">
          <DetailRow label="GST number" value={hostel.gstNumber} />
          <DetailRow label="Registration number" value={hostel.registrationNumber} />
          <DetailRow label="Subscription plan" value={hostel.subscriptionPlan} />
          <DetailRow label="Subdomain" value={hostel.subdomain} />
        </div>
      </section>

      <section className="hostel-profile-card">
        <h2>Bank details</h2>
        <div className="hostel-profile-grid">
          <DetailRow label="Account holder" value={hostel.bankAccountHolder} />
          <DetailRow label="Bank name" value={hostel.bankName} />
          <DetailRow label="Account number" value={hostel.bankAccountNumber} />
          <DetailRow label="IFSC code" value={hostel.bankIfscCode} />
        </div>
      </section>

      <section className="hostel-profile-card">
        <h2>Overview</h2>
        <div className="hostel-profile-grid">
          <DetailRow label="Floors" value={overview?.floors} />
          <DetailRow label="Rooms" value={overview?.rooms} />
          <DetailRow label="Total beds" value={overview?.totalBeds} />
          <DetailRow label="Occupied beds" value={overview?.occupiedBeds} />
          <DetailRow label="Available beds" value={overview?.availableBeds} />
          <DetailRow label="Currency" value={hostel.currency} />
          <DetailRow label="Timezone" value={hostel.timezone} />
        </div>
      </section>
    </div>
  );
};

export default Profile;
